/**
 * Contract Checker — Final Review screen (Step 2 of 2).
 *
 * Adapted from the Stitch design (project 17950727036856797166,
 * screen b8c0b0cf8eaf4ff5a692ed5f34b355f2). The Stitch markup
 * included its own top navigation bar — stripped here because the
 * page renders inside `OrgLayoutPage` which already provides the
 * standard `OrgSidebar` + `OrgTopbar`.
 *
 * Orchestrator responsibilities:
 *   - holds the editable local copy of `findings` (Proposed Change +
 *     Full Counter-Clause can be edited inline before download)
 *   - tracks which (findingId, field) pair is currently in edit mode
 *   - holds the shared SELECTION set (by finding id) that drives the
 *     download, plus the two-tab view (User-specific query / Overall)
 *
 * The "User specific query" tab is driven by `matchedIds` — the findings
 * the AI flagged at analysis time as relevant to the user's Topics query
 * (entered on the upload/setup step). There is no re-query here.
 *
 * Visual chrome is delegated to small sub-components in this folder:
 * `ReviewSummary`, `ClauseCard`. The "Download Review" button sits in the tab
 * row (justified opposite the tabs).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ClauseCard } from './clause-card';
import { ReviewSummary } from './review-summary';
import type {
  ClauseFinding,
  EditableField,
  FinalReviewData,
} from '../types/contract-checker.types';

// Re-export the public types so callers can import them from
// `final-review` (the page does this today) without knowing about the
// internal `types/` directory.
export type { ClauseFinding, FinalReviewData, RiskLevel } from '../types/contract-checker.types';

interface FinalReviewProps {
  data: FinalReviewData;
  /** Where the "Back to Workspace" link goes (defaults to the upload step). */
  backHref: string;
  /**
   * Click handler for "Download Review". Receives the *selected*,
   * user-edited findings so only what's checked flows into the PDF
   * (deduped by id). May return a Promise — the button shows a spinner.
   */
  onDownload?: (findings: ClauseFinding[]) => void | Promise<void>;
  /** Fired whenever the user saves an inline edit (lets the page mirror state). */
  onChangeFindings?: (findings: ClauseFinding[]) => void;
  /** The user's Topics query (shown as context on the query tab). */
  query?: string;
  /**
   * Ids of findings the AI flagged as relevant to `query`. `null` = no
   * stored matches (legacy row); `[]` = analysed but nothing matched.
   */
  matchedIds?: string[] | null;
}

type ResultTab = 'query' | 'overall';

export function FinalReview({
  data,
  backHref,
  onDownload,
  onChangeFindings,
  query,
  matchedIds = null,
}: FinalReviewProps) {
  const { t } = useTranslation();

  // Local editable copy of findings. Re-initialised whenever the upstream
  // `data.findings` ref changes (e.g. the page swaps in a different analysis).
  const [findings, setFindings] = useState<ClauseFinding[]>(data.findings);
  useEffect(() => {
    setFindings(data.findings);
  }, [data.findings]);

  // Stable list of ids from the source analysis — used to (re)seed
  // selection without re-firing on every inline edit (which mutates the
  // local `findings` array ref but never changes ids).
  const allIds = useMemo(() => data.findings.map((f) => f.id), [data.findings]);

  const matchedSet = useMemo(() => (matchedIds ? new Set(matchedIds) : null), [matchedIds]);
  const hasMatches = (matchedIds?.length ?? 0) > 0;

  // ---- Shared selection (drives the download, deduped by id) -------------
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(allIds));
  // Preselect rule: if the AI flagged matches, preselect exactly those;
  // otherwise select everything (so a contract with no specific Topics still
  // downloads in full). Keyed on matchedIds/allIds only — never on edits.
  useEffect(() => {
    if (matchedIds && matchedIds.length > 0) {
      setSelectedIds(new Set(matchedIds));
    } else {
      setSelectedIds(new Set(allIds));
    }
  }, [matchedIds, allIds]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Tabs --------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<ResultTab>(hasMatches ? 'query' : 'overall');
  // Surface the query tab once matches are present (e.g. after the row loads).
  useEffect(() => {
    if (hasMatches) setActiveTab('query');
  }, [hasMatches]);

  // ---- Inline edit (one cell at a time) ----------------------------------
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [editing, setEditing] = useState<{
    findingId: string;
    field: EditableField;
    draft: string;
  } | null>(null);
  const startEdit = (findingId: string, field: EditableField, currentValue: string) => {
    setEditing({ findingId, field, draft: currentValue });
  };
  const updateDraft = (next: string) => {
    setEditing((prev) => (prev ? { ...prev, draft: next } : prev));
  };
  const cancelEdit = () => setEditing(null);
  const saveEdit = () => {
    if (!editing) return;
    const next = findings.map((f) =>
      f.id === editing.findingId ? { ...f, [editing.field]: editing.draft } : f
    );
    setFindings(next);
    onChangeFindings?.(next);
    setEditing(null);
  };

  // ---- Download ----------------------------------------------------------
  const [isDownloading, setIsDownloading] = useState(false);
  const selectedFindings = useMemo(
    () => findings.filter((f) => selectedIds.has(f.id)),
    [findings, selectedIds]
  );
  const handleDownloadClick = async () => {
    if (!onDownload || isDownloading || selectedFindings.length === 0) return;
    setIsDownloading(true);
    try {
      await onDownload(selectedFindings);
    } finally {
      setIsDownloading(false);
    }
  };

  // ---- Rendering ---------------------------------------------------------
  const queryFindings = useMemo(
    () => (matchedSet ? findings.filter((f) => matchedSet.has(f.id)) : []),
    [findings, matchedSet]
  );

  const renderList = (list: ClauseFinding[]) => (
    <div className="space-y-4">
      {list.map((finding) => {
        const isThisEditing = editing?.findingId === finding.id;
        return (
          <ClauseCard
            key={finding.id}
            finding={finding}
            isSelected={selectedIds.has(finding.id)}
            onToggleSelect={() => toggleSelect(finding.id)}
            isMatched={matchedSet?.has(finding.id) ?? false}
            isOpen={expanded.has(finding.id)}
            onToggle={() => toggleExpanded(finding.id)}
            editingField={isThisEditing ? editing.field : null}
            draft={isThisEditing ? editing.draft : ''}
            onStartEdit={(field, currentValue) => startEdit(finding.id, field, currentValue)}
            onDraftChange={updateDraft}
            onSave={saveEdit}
            onCancel={cancelEdit}
          />
        );
      })}
    </div>
  );

  return (
    <div className="font-nunito pb-8">
      <div className="w-full min-w-0 max-w-4xl mx-auto">
        <ReviewSummary data={data} backHref={backHref} />

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ResultTab)}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="query">
                {t('ORGCONTRACTCHECKER_RESULT_TAB_QUERY', { defaultValue: 'Focus Areas' })}
              </TabsTrigger>
              <TabsTrigger value="overall">
                {t('ORGCONTRACTCHECKER_RESULT_TAB_OVERALL', { defaultValue: 'All Findings' })}
              </TabsTrigger>
            </TabsList>

            {onDownload && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleDownloadClick}
                disabled={isDownloading || selectedFindings.length === 0}
                className="gap-2"
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {isDownloading
                  ? t('ORGCONTRACTCHECKER_RESULT_DOWNLOADING', { defaultValue: 'Preparing PDF…' })
                  : t('ORGCONTRACTCHECKER_RESULT_DOWNLOAD', { defaultValue: 'Download Review' })}
              </Button>
            )}
          </div>

          <TabsContent value="query">
            {query?.trim() && (
              <p className="mb-4 text-sm text-globe-grey">
                {t('ORGCONTRACTCHECKER_RESULT_QUERY_CONTEXT', {
                  defaultValue: 'Findings related to your topics:',
                })}{' '}
                <span className="font-medium text-[hsl(var(--fg))]">{query.trim()}</span>
              </p>
            )}

            {!matchedSet || queryFindings.length === 0 ? (
              <p className="text-sm text-globe-grey py-8 text-center">
                {query?.trim()
                  ? t('ORGCONTRACTCHECKER_RESULT_QUERY_NO_MATCHES', {
                      defaultValue: 'No findings matched your topics. See the Overall tab.',
                    })
                  : t('ORGCONTRACTCHECKER_RESULT_QUERY_NO_TOPICS', {
                      defaultValue:
                        'No specific topics were set for this analysis. See the Overall tab.',
                    })}
              </p>
            ) : (
              renderList(queryFindings)
            )}
          </TabsContent>

          <TabsContent value="overall">{renderList(findings)}</TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default FinalReview;
